import { z } from 'zod';
import { EQUIPMENT_KINDS, EQUIPMENT_STATUSES, EXPENSE_METHODS, ROLES, TICKET_EVENT_KINDS, TICKET_PRIORITIES, TICKET_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, localeSchema, patchOf, searchQuerySchema, sortOrderSchema } from './common';

const textOrNull = (max: number) => z.string().trim().max(max).nullable().optional();

export const createEquipmentSchema = z.object({
  name: z.string().trim().min(1).max(120),
  kind: z.enum(EQUIPMENT_KINDS).default('OTHER'),
  brand: z.string().trim().max(80).nullable().optional(),
  model: z.string().trim().max(80).nullable().optional(),
  serialNumber: z.string().trim().max(80).nullable().optional(),
  locationId: idSchema.nullable().optional().describe('The branch it stands at; the branch the request works at when omitted'),
  roomId: idSchema.nullable().optional().describe('A room of that branch'),
  purchaseDate: dateOnlySchema.nullable().optional(),
  priceMinor: z.number().int().min(0).nullable().optional().describe('What it cost, in minor units of the gym currency'),
  warrantyUntil: dateOnlySchema.nullable().optional(),
  status: z.enum(EQUIPMENT_STATUSES).default('IN_SERVICE'),
  serviceIntervalDays: z.number().int().min(1).max(3650).nullable().optional().describe('Planned service every this many days; null for none'),
  lastServicedOn: dateOnlySchema.nullable().optional().describe('The gym day it was last serviced; the next service falls due serviceIntervalDays after it, or after the purchase date'),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type CreateEquipmentInput = z.infer<typeof createEquipmentSchema>;

export const updateEquipmentSchema = patchOf(createEquipmentSchema);
export type UpdateEquipmentInput = z.infer<typeof updateEquipmentSchema>;

export const EQUIPMENT_LIST_SORTS = ['name', 'kind', 'status', 'purchaseDate', 'warrantyUntil', 'nextServiceDueOn'] as const;
export type EquipmentListSort = (typeof EQUIPMENT_LIST_SORTS)[number];

const allBranchesSchema = z.stringbool().optional().describe("Every branch's; without it and without locationId, the list keeps to the branch the request acts at (`X-Location-Id`), machines kept for the whole gym included");

export const equipmentListQuerySchema = searchQuerySchema.extend({
  locationId: idSchema.optional(),
  allBranches: allBranchesSchema,
  kind: z.enum(EQUIPMENT_KINDS).optional(),
  status: z.enum(EQUIPMENT_STATUSES).optional(),
  sort: z.enum(EQUIPMENT_LIST_SORTS).default('name'),
  order: sortOrderSchema.default('asc'),
});
export type EquipmentListQuery = z.infer<typeof equipmentListQuerySchema>;

export const equipmentSchema = z.object({
  id: idSchema,
  name: z.string(),
  kind: z.enum(EQUIPMENT_KINDS),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  serialNumber: z.string().nullable(),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  roomId: idSchema.nullable(),
  roomName: z.string().nullable(),
  purchaseDate: z.string().nullable(),
  priceMinor: z.number().int().nullable().describe('Null unless the caller holds equipment.manage'),
  warrantyUntil: z.string().nullable(),
  status: z.enum(EQUIPMENT_STATUSES),
  serviceIntervalDays: z.number().int().nullable(),
  lastServicedOn: z.string().nullable(),
  nextServiceDueOn: z.string().nullable().describe('When the next planned service falls due: the last service (or the purchase, or when the machine was added) plus the interval; null with no interval or once retired'),
  notes: z.string().nullable(),
  photoUrl: z.string().nullable().describe('A signed link that expires after a few minutes'),
  openTickets: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EquipmentDto = z.infer<typeof equipmentSchema>;

export const equipmentLabelQuerySchema = z.object({ locale: localeSchema.default('en') });
export type EquipmentLabelQuery = z.infer<typeof equipmentLabelQuerySchema>;

export const EQUIPMENT_LABELS_MAX = 500;

export const equipmentLabelsQuerySchema = equipmentListQuerySchema.omit({ page: true, pageSize: true, sort: true, order: true }).extend({ locale: localeSchema.default('en') });
export type EquipmentLabelsQuery = z.infer<typeof equipmentLabelsQuerySchema>;

export const logTicketExpenseSchema = z.object({
  method: z.enum(EXPENSE_METHODS),
  note: textOrNull(500),
});
export type LogTicketExpenseInput = z.infer<typeof logTicketExpenseSchema>;

export const TICKET_ASSIGNEE_ME = 'me';
export const TICKET_ASSIGNEE_NONE = 'none';

export const createTicketSchema = z.object({
  equipmentId: idSchema,
  description: z.string().trim().min(1).max(2000),
  priority: z.enum(TICKET_PRIORITIES).default('NORMAL'),
  markOutOfOrder: z.boolean().default(false).describe('Also mark the machine out of order, so everyone sees it is not to be used'),
});
export type CreateTicketInput = z.infer<typeof createTicketSchema>;

const ticketFieldsSchema = z.object({
  description: z.string().trim().min(1).max(2000),
  priority: z.enum(TICKET_PRIORITIES),
  assigneeId: idSchema.nullable().describe('The staff member working it, from `GET /maintenance/assignees`'),
  costMinor: z.number().int().min(0).nullable(),
});

export const updateTicketSchema = patchOf(ticketFieldsSchema);
export type UpdateTicketInput = z.infer<typeof updateTicketSchema>;

export const ticketStatusSchema = z.object({
  status: z.enum(TICKET_STATUSES),
  resolutionNote: z.string().trim().max(2000).nullable().optional().describe('Required for WONT_FIX'),
  costMinor: z.number().int().min(0).nullable().optional().describe('What the work cost; leaves the recorded cost alone when omitted'),
  returnToService: z.boolean().default(false).describe('When this closes the last open ticket of an out-of-order machine, put it back in service'),
});
export type TicketStatusInput = z.infer<typeof ticketStatusSchema>;

export const ticketNoteSchema = z.object({ text: z.string().trim().min(1).max(2000) });
export type TicketNoteInput = z.infer<typeof ticketNoteSchema>;

export const TICKET_LIST_SORTS = ['createdAt', 'priority', 'status', 'statusChangedAt'] as const;
export type TicketListSort = (typeof TICKET_LIST_SORTS)[number];

const ticketFiltersShape = {
  priority: z.enum(TICKET_PRIORITIES).optional(),
  assigneeId: z.union([idSchema, z.literal(TICKET_ASSIGNEE_NONE), z.literal(TICKET_ASSIGNEE_ME)]).optional().describe("A staff id, `none` for unassigned tickets or `me` for the caller's"),
  locationId: idSchema.optional(),
  allBranches: allBranchesSchema,
  equipmentId: idSchema.optional(),
};

export const ticketListQuerySchema = searchQuerySchema.extend({
  ...ticketFiltersShape,
  status: z.enum(TICKET_STATUSES).optional(),
  open: z.stringbool().optional().describe('Only tickets still being dealt with: open or in progress'),
  sort: z.enum(TICKET_LIST_SORTS).default('createdAt'),
  order: sortOrderSchema.default('desc'),
});
export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;

export const ticketBoardQuerySchema = z.object({ q: z.string().trim().max(120).optional(), ...ticketFiltersShape });
export type TicketBoardQuery = z.infer<typeof ticketBoardQuerySchema>;

export const maintenanceAssigneeSchema = z.object({
  id: idSchema,
  userId: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type MaintenanceAssigneeDto = z.infer<typeof maintenanceAssigneeSchema>;

export const ticketSchema = z.object({
  id: idSchema,
  equipmentId: idSchema,
  equipmentName: z.string(),
  equipmentStatus: z.enum(EQUIPMENT_STATUSES),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  description: z.string().nullable().describe('Null on a planned service, which the browser words from plannedDueOn'),
  plannedDueOn: z.string().nullable(),
  priority: z.enum(TICKET_PRIORITIES),
  status: z.enum(TICKET_STATUSES),
  reportedBy: z.string().nullable().describe('Null for the daily job'),
  assignee: maintenanceAssigneeSchema.omit({ role: true }).nullable(),
  costMinor: z.number().int().nullable().describe('Null unless the caller holds maintenance.manage'),
  expenseId: idSchema.nullable().describe('The expense its cost was logged as, for maintenance.manage; null when none stands, a voided one included'),
  resolutionNote: z.string().nullable(),
  hasPhoto: z.boolean(),
  statusChangedAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  openOnEquipment: z.number().int().describe('Open and in-progress tickets on the same machine, this one included while it is open'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TicketDto = z.infer<typeof ticketSchema>;

/** Values a timeline entry carries for the browser to word. */
export const ticketEventDetailsSchema = z.object({
  fields: z.array(z.string()).optional(),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  assigneeName: z.string().nullable().optional(),
  equipmentStatus: z.enum(EQUIPMENT_STATUSES).optional(),
  plannedDueOn: z.string().optional(),
  costMinor: z.number().int().optional(),
});
export type TicketEventDetails = z.infer<typeof ticketEventDetailsSchema>;

export const ticketEventSchema = z.object({
  id: idSchema,
  kind: z.enum(TICKET_EVENT_KINDS),
  fromStatus: z.enum(TICKET_STATUSES).nullable(),
  toStatus: z.enum(TICKET_STATUSES).nullable(),
  text: z.string().nullable(),
  details: ticketEventDetailsSchema,
  actorName: z.string().nullable().describe('Who did it; null for the daily job'),
  createdAt: z.string(),
});
export type TicketEventDto = z.infer<typeof ticketEventSchema>;

export const ticketDetailSchema = ticketSchema.extend({
  photoUrl: z.string().nullable().describe('A signed link that expires after a few minutes'),
  events: z.array(ticketEventSchema).describe('Newest first'),
});
export type TicketDetailDto = z.infer<typeof ticketDetailSchema>;

export const ticketBoardSchema = z.object({
  columns: z.array(
    z.object({
      status: z.enum(TICKET_STATUSES),
      total: z.number().int(),
      items: z.array(ticketSchema).describe('Open and in progress: urgent first, then the longest waiting. Closed: the most recently closed first'),
    }),
  ),
  limit: z.number().int(),
});
export type TicketBoardDto = z.infer<typeof ticketBoardSchema>;
